import type { WorldInit } from '../app/initializeWorld';
import type { ControlState } from '../controls/types';
import type { CityTime } from '../time/cityTime';
import { bindSceneClock } from '../app/sceneClock';
import { createVisitorAvatars } from './avatars';
import { CityClient } from './client';
import type { components } from './api.generated';
import type { PoseCommand, Snapshot } from './protocol';
import type { EmoteName } from './emotes';

function currentPose(
  state: ControlState,
  profile: Pick<components['schemas']['Visitor'], 'character' | 'name' | 'clothing'>
): Omit<PoseCommand, 'type' | 'seq'> {
  const { pose, mode, transition } = state;
  return {
    ...profile,
    x: pose.position[0],
    y: pose.position[1],
    z: pose.position[2],
    yaw: pose.yaw,
    mode: mode === 'boat' || transition?.targetMode === 'boat' ? 'boat' : 'walk',
  };
}

export function createMultiplayer(
  init: WorldInit,
  time: CityTime,
  control: () => ControlState,
  label: (value: string) => void,
  profile: Pick<components['schemas']['Visitor'], 'character' | 'name' | 'clothing'>,
  showNames: () => boolean,
  joined: (players: Snapshot['players']) => void,
  refused: () => void
) {
  const avatars = createVisitorAvatars(init, 128, 'online-visitors', showNames);
  const client = new CityClient(
    () => currentPose(control(), profile),
    (clock, players) => {
      time.follow(() => clock.cityEpoch());
      bindSceneClock(() => clock.animationSeconds());
      joined(players);
    },
    label,
    refused
  );
  client.start();
  return {
    lights: avatars.lights,
    update(night = 0): void {
      avatars.update(
        client.peers.sample(client.clock.serverEpoch()),
        client.clock.animationSeconds(),
        night,
        true,
        client.clock.serverEpoch()
      );
    },
    diagnostics: () => client.diagnostics(),
    visitors: () => avatars.diagnostics(),
    speech: client.speech,
    say: (text: string, recipient: string | null = null) => client.chat.send(text, recipient),
    muted: (id: string) => client.mutes.muted.has(id),
    mute: (id: string, muted: boolean) => client.mutes.set(id, muted),
    emote: (name: EmoteName) => client.gestures.send(name),
    currentGesture: () => client.currentGesture(),
    speakers: () => client.peers.sample(client.clock.serverEpoch()),
    serverEpoch: () => client.clock.serverEpoch(),
    dispose(): void {
      client.dispose();
      avatars.dispose();
      bindSceneClock(null);
    },
  };
}
